import type { IconName } from "../../components/Icon";
import type { NavItem } from "../../components/shell/PortalLayout";
import type { PaletteItem } from "../../components/shell/CommandPalette";
import { t } from "../../lib/sprache";

/**
 * Navigation der Betreiber-Konsole (Vertrieb-Konsole, Inhaber 02.11.2026): linke Seitenleiste im Portal-Design statt der
 * Knopfgruppen, gegliedert nach Start, Vertrieb, Kunden, Support, Marketing, Technik und Team und nach Rolle gefiltert.
 * Die Rolle steuert nur, was angeboten wird; jede Abfrage prüft die Datenbank erneut (app.is_platform_developer,
 * app.is_platform_admin bzw. app.is_platform_staff).
 * Rollen (0500, Inhaber 03.11.2026): Entwicklung (developer) darf alles, Admin ist die Betriebsrolle (Kunden, Tarife, Sperren,
 * Testphasen, Rabattcodes, Support, Demo, Vertrieb einladen), Vertrieb wie bisher. Technik, KI-Protokoll und Rechtstexte nur
 * die Entwicklung; Produkt-Standards sieht der Admin nur mit den Betriebsfristen.
 */
export type KonsolenRolle = "developer" | "admin" | "sales";

/** Rang wie app.platform_rank(): Entwicklung 3, Admin 2, Vertrieb 1 */
export const RANG: Record<KonsolenRolle, number> = { developer: 3, admin: 2, sales: 1 };
/** Betriebsrechte (Admin oder Entwicklung), wie app.is_platform_admin() */
export const istBetrieb = (rolle: KonsolenRolle): boolean => RANG[rolle] >= 2;
/** Systemrechte (nur Entwicklung), wie app.is_platform_developer() */
export const istEntwicklung = (rolle: KonsolenRolle): boolean => rolle === "developer";
/** Betriebsfristen, die auch der Admin setzt (wie app.platform_default_betrieb, 0500); alle anderen Standards nur die Entwicklung */
export const BETRIEBSFRISTEN: readonly string[] = ["trial_days", "trial_plan", "max_open_trials", "payment_bonus_days", "subscription_grace_days",
  "subscription_notice_days", "read_only_min_days", "purge_notice_days", "account_deletion_days",
  // 0590: Bankverbindung für das Abo auf Rechnung (wie app.platform_default_betrieb)
  "invoice_payment_iban", "invoice_payment_bic", "invoice_payment_holder"];
/** Bezeichnung der Rolle in der Konsole */
export const ROLLEN_NAME: Record<KonsolenRolle, string> = { developer: "Entwicklung", admin: "Admin", sales: "Vertrieb" };

interface Bereich {
  to: string;
  label: string;
  icon: IconName;
  gruppe: string | null;
  rollen: readonly KonsolenRolle[];
  also?: string[];
  end?: boolean;
  /** Stichworte für die Suche (⌘K) */
  suche?: string;
}

const ALLE = ["developer", "admin", "sales"] as const;
const BETRIEB = ["developer", "admin"] as const;
const ENTWICKLUNG = ["developer"] as const;

export const BEREICHE: readonly Bereich[] = [
  { to: "/intern", label: "Start", icon: "grid", gruppe: null, rollen: ALLE, end: true, suche: "Übersicht Heute" },
  // 0530: Team-Kalender für alle Rollen (Vertrieb legt dort keine Demo-Zeitfenster an)
  { to: "/intern/kalender", get label() { return t("Kalender", "Calendar"); }, icon: "calendar", gruppe: null, rollen: ALLE, suche: "Termine Calls Abwesenheit Urlaub Team" },
  { to: "/intern/pipeline", label: "Pipeline", icon: "chart", gruppe: "Vertrieb", rollen: ALLE, suche: "Stufen Leads" },
  { to: "/intern/leads", label: "Leads", icon: "list", gruppe: "Vertrieb", rollen: ALLE, suche: "Interessenten Kontakte" },
  { to: "/intern/demo-termine", label: "Demo-Anfragen & Termine", icon: "calendar", gruppe: "Vertrieb", rollen: ALLE, suche: "Buchungen Zeitfenster" },
  { to: "/intern/demo", label: "Demo-Konto", icon: "play", gruppe: "Vertrieb", rollen: ALLE, suche: "Demo öffnen zurücksetzen" },
  { to: "/intern/agenturen", label: "Konten", icon: "users", gruppe: "Kunden", rollen: ALLE, suche: "Agenturen Kunden Kundenakte" },
  { to: "/intern/antraege", label: "Anträge", icon: "file", gruppe: "Kunden", rollen: BETRIEB, suche: "Tarifanfragen Kontolöschung" },
  // 0590: Abo auf Rechnung
  { to: "/intern/zahlung-auf-rechnung", label: "Zahlung auf Rechnung", icon: "receipt", gruppe: "Kunden", rollen: BETRIEB, suche: "Überweisung Payoneer IBAN Zahlungseingang von Hand verbuchen" },
  { to: "/intern/tickets", label: "Tickets", icon: "chat", gruppe: "Support", rollen: BETRIEB },
  { to: "/intern/fehler", label: "Gemeldete Fehler", icon: "help", gruppe: "Support", rollen: BETRIEB },
  { to: "/intern/support", label: "Support-Zugang", icon: "lock", gruppe: "Support", rollen: BETRIEB },
  { to: "/intern/ki-protokoll", label: "KI-Protokoll", icon: "eye", gruppe: "Support", rollen: ENTWICKLUNG },
  { to: "/intern/traffic", label: "Website-Traffic", icon: "insights", gruppe: "Marketing", rollen: BETRIEB, suche: "Besucher Reichweite" },
  { to: "/intern/rabattcodes", label: "Rabattcodes", icon: "tag", gruppe: "Marketing", rollen: BETRIEB },
  { to: "/intern/instagram", label: "Instagram", icon: "smile", gruppe: "Marketing", rollen: BETRIEB },
  { to: "/intern/kuendigungsweg", label: "Kündigungsweg", icon: "withdraw", gruppe: "Marketing", rollen: BETRIEB },
  { to: "/intern/ueberwachung", label: "Betrieb", icon: "settings", gruppe: "Technik", rollen: ENTWICKLUNG, suche: "Zeitpläne Mailversand Serverfunktionen" },
  { to: "/intern/plattform", label: "Produkt-Standards", icon: "edit", gruppe: "Technik", rollen: BETRIEB },
  { to: "/intern/rechtstexte", label: "Rechtstexte", icon: "file", gruppe: "Technik", rollen: ENTWICKLUNG },
  { to: "/intern/ki-zugaenge", label: "KI-Zugänge", icon: "shield", gruppe: "Technik", rollen: ENTWICKLUNG },
  { to: "/intern/basiszins", label: "Basiszins", icon: "receipt", gruppe: "Technik", rollen: ENTWICKLUNG },
  { to: "/intern/team", label: "Team", icon: "user", gruppe: "Team", rollen: BETRIEB, suche: "Mitarbeiter Einladung Rollen" },
];

export const KONSOLE_MEHR = "/intern/mehr";
/** Anmeldung & Sicherheit im internen Bereich (eigene Sitzung, daher nicht /konto/sicherheit des Kundenportals) */
export const KONSOLE_SICHERHEIT = "/intern/sicherheit";

export function bereicheFuer(rolle: KonsolenRolle): Bereich[] {
  return BEREICHE.filter((b) => b.rollen.includes(rolle));
}

/** Darf diese Rolle den Pfad öffnen? Detailseiten zählen zu ihrem Bereich (/intern/agenturen/… zu Konten). */
export function erlaubt(rolle: KonsolenRolle, pfad: string): boolean {
  const p = pfad.replace(/\/+$/, "") || "/intern";
  // Anmeldung & Sicherheit (zweiter Faktor) der internen Sitzung: für jede Rolle
  if (p === "/intern" || p === KONSOLE_MEHR || p === KONSOLE_SICHERHEIT) return true;
  return bereicheFuer(rolle).some((b) => b.to !== "/intern" && (p === b.to || p.startsWith(`${b.to}/`)));
}

/** Seitenleiste: Gruppenüberschrift am ersten Eintrag jeder Gruppe; auf einer Kundenakte zusätzlich „Kundenakte“ */
export function konsoleNav(rolle: KonsolenRolle, kundenakte: string | null = null): NavItem[] {
  const out: NavItem[] = [];
  let letzte: string | null = null;
  for (const b of bereicheFuer(rolle)) {
    const item: NavItem = { label: b.label, to: b.to, icon: b.icon, ...(b.end ? { end: true } : {}), ...(b.also ? { also: b.also } : {}) };
    if (b.gruppe && b.gruppe !== letzte) item.group = b.gruppe;
    letzte = b.gruppe;
    out.push(item);
    if (b.to === "/intern/agenturen" && kundenakte) out.push({ label: "Kundenakte", to: kundenakte, icon: "user", end: true });
  }
  return out;
}

/** Untere Leiste am Handy: Start, Pipeline, Leads, Konten und „Mehr“ mit allen übrigen Bereichen */
export function konsoleBottom(rolle: KonsolenRolle): NavItem[] {
  const haupt = ["/intern", "/intern/pipeline", "/intern/leads", "/intern/agenturen"];
  const alle = bereicheFuer(rolle);
  const main = haupt.map((to) => alle.find((b) => b.to === to)).filter((b): b is Bereich => !!b)
    .map((b) => ({ label: b.label, to: b.to, icon: b.icon, ...(b.end ? { end: true } : {}) }));
  const rest = alle.filter((b) => !haupt.includes(b.to)).map((b) => b.to);
  return [...main, { label: "Mehr", to: KONSOLE_MEHR, icon: "menu", also: rest }];
}

/** Seiten für die Suche (⌘K) */
export function konsolePalette(rolle: KonsolenRolle): PaletteItem[] {
  return bereicheFuer(rolle).map((b) => ({ label: b.gruppe ? `${b.gruppe} · ${b.label}` : b.label, to: b.to, ...(b.suche ? { hint: b.suche } : {}) }));
}

/** Gruppen für die Seite „Mehr“ (Handy) */
export function konsoleGruppen(rolle: KonsolenRolle): { gruppe: string; bereiche: Bereich[] }[] {
  const out: { gruppe: string; bereiche: Bereich[] }[] = [];
  for (const b of bereicheFuer(rolle)) {
    const g = b.gruppe ?? "Start";
    const last = out[out.length - 1];
    if (last && last.gruppe === g) last.bereiche.push(b);
    else out.push({ gruppe: g, bereiche: [b] });
  }
  return out;
}
